import { useState } from "react";
import { CheckCircle2, Crown, ArrowRight, Tag, X } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LIST_PRICE = 19;

export default function UpgradeModal({ onClose, onUpgrade, checkoutLoading }) {
  const [code, setCode] = useState("");
  const [validating, setValidating] = useState(false);
  const [discount, setDiscount] = useState(0); // dollar amount
  const [appliedCode, setAppliedCode] = useState("");
  const [error, setError] = useState("");

  const applyCode = async () => {
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) return;
    setValidating(true);
    setError("");
    try {
      const { data } = await axios.post(
        `${API}/payments/validate-code`,
        { code: trimmed, product: "premium_guide" },
        { withCredentials: true }
      );
      if (data.valid) {
        setDiscount(Number(data.amount || 0));
        setAppliedCode(trimmed);
        setError("");
      } else {
        setDiscount(0);
        setAppliedCode("");
        setError(data.error || "Invalid code");
      }
    } catch (e) {
      setError(e.response?.data?.error || e.response?.data?.detail || "Could not validate code");
      setDiscount(0);
      setAppliedCode("");
    }
    setValidating(false);
  };

  const clearCode = () => {
    setCode("");
    setDiscount(0);
    setAppliedCode("");
    setError("");
  };

  const finalPrice = Math.max(LIST_PRICE - discount, 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        data-testid="upgrade-modal"
        className="w-full max-w-md rounded-xl border-2 border-[var(--c-border)] bg-[var(--c-surface)] p-8 shadow-[6px_6px_0px_var(--c-shadow)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-xl border-2 border-[var(--c-border)] bg-[#1E3A8A] shadow-[3px_3px_0px_var(--c-shadow)]">
            <Crown className="h-7 w-7 text-white" />
          </div>
          <h2 className="font-heading text-2xl font-bold text-[var(--c-text)]">Unlock Full Guide</h2>
          <p className="mt-2 text-[var(--c-muted)]">
            Steps 3-9 and the Cost Estimator are part of the premium guide. Upgrade to complete your LLC formation journey.
          </p>

          <div className="my-6 rounded-xl border-2 border-[var(--c-border)] bg-[var(--c-bg)] p-5">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--c-muted)]">One-Time Payment</p>
            {discount > 0 ? (
              <div>
                <p className="font-heading text-lg font-bold text-[var(--c-muted)] line-through">${LIST_PRICE}</p>
                <p data-testid="discounted-price" className="font-heading text-4xl font-black text-[#1E3A8A]">${finalPrice}</p>
                <p className="mt-1 text-xs font-bold uppercase tracking-[0.15em] text-[#1E3A8A]">
                  {appliedCode} applied — saved ${discount}
                </p>
              </div>
            ) : (
              <p className="font-heading text-4xl font-black text-[var(--c-text)]">${LIST_PRICE}</p>
            )}
            <p className="mt-1 text-sm text-[var(--c-muted)]">Lifetime access &middot; No subscription</p>
          </div>

          {/* Discount code */}
          <div data-testid="discount-code-section" className="mb-5 text-left">
            <label className="mb-1.5 flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.15em] text-[var(--c-muted)]">
              <Tag className="h-3 w-3" />
              Have a discount code?
            </label>
            <div className="flex gap-2">
              <input
                data-testid="discount-code-input"
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyCode(); } }}
                placeholder="Enter code (e.g., LAUNCH30)"
                disabled={!!appliedCode}
                className="flex-1 rounded-lg border-2 border-[var(--c-border)] bg-[var(--c-surface)] px-3 py-2 font-mono text-sm uppercase text-[var(--c-text)] placeholder:text-[var(--c-muted)] placeholder:font-sans focus:border-[#1E3A8A] focus:outline-none disabled:opacity-60"
              />
              {appliedCode ? (
                <button
                  data-testid="clear-discount-code"
                  onClick={clearCode}
                  className="flex items-center justify-center gap-1 rounded-lg border-2 border-[var(--c-border)] bg-[var(--c-bg)] px-3 py-2 text-xs font-bold text-[var(--c-muted)] transition-all hover:-translate-y-0.5 hover:text-[var(--c-text)]"
                >
                  <X className="h-3.5 w-3.5" />
                  Remove
                </button>
              ) : (
                <button
                  data-testid="apply-discount-code"
                  onClick={applyCode}
                  disabled={!code.trim() || validating}
                  className="rounded-lg border-2 border-[var(--c-border)] bg-[var(--c-bg)] px-4 py-2 text-xs font-bold text-[var(--c-text)] transition-all hover:-translate-y-0.5 hover:shadow-[2px_2px_0px_var(--c-shadow)] disabled:opacity-50"
                >
                  {validating ? "..." : "Apply"}
                </button>
              )}
            </div>
            {error && <p data-testid="discount-code-error" className="mt-1.5 text-xs font-medium text-[#FF4D4D]">{error}</p>}
            {appliedCode && discount > 0 && (
              <p data-testid="discount-code-success" className="mt-1.5 text-xs font-medium text-[#1E3A8A]">
                ✓ Code applied — saving ${discount}
              </p>
            )}
          </div>

          <ul className="mb-6 space-y-2 text-left text-sm">
            <li className="flex items-center gap-2 text-[var(--c-text)]"><CheckCircle2 className="h-4 w-4 text-[#A3E6D5]" /> Steps 3-9: Filing, Operating Agreement, EIN, Banking, Taxes, Licenses, Compliance</li>
            <li className="flex items-center gap-2 text-[var(--c-text)]"><CheckCircle2 className="h-4 w-4 text-[#A3E6D5]" /> Interactive Startup Cost Estimator</li>
            <li className="flex items-center gap-2 text-[var(--c-text)]"><CheckCircle2 className="h-4 w-4 text-[#A3E6D5]" /> All external links & pro tips</li>
            <li className="flex items-center gap-2 text-[var(--c-text)]"><CheckCircle2 className="h-4 w-4 text-[#A3E6D5]" /> Progress saved forever</li>
          </ul>

          <button
            data-testid="upgrade-pay-button"
            onClick={() => onUpgrade(appliedCode || undefined)}
            disabled={checkoutLoading}
            className="flex w-full items-center justify-center gap-2 rounded-full border-2 border-[var(--c-border)] bg-[#1E3A8A] px-6 py-3.5 font-bold text-white shadow-[4px_4px_0px_var(--c-shadow)] transition-all hover:-translate-y-1 hover:shadow-[6px_6px_0px_var(--c-shadow)] disabled:opacity-60"
          >
            {checkoutLoading ? "Redirecting to checkout..." : `Unlock for $${finalPrice}`}
            {!checkoutLoading && <ArrowRight className="h-5 w-5" />}
          </button>

          <button
            onClick={onClose}
            className="mt-4 text-sm font-medium text-[var(--c-muted)] hover:text-[var(--c-text)]"
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
}
